import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, Icon, Modal } from 'semantic-ui-react';
import { fetchAllResidentGroups } from '../../services/Groups';
import { Group } from '../../types/Group';
import { ALLOWED_TYPES, MAX_FILE_SIZE } from '../../utils/fileValidation';

interface PostFormModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (content: string, imageFile: File | null, removeImage: boolean, groupIds: string[]) => Promise<void>;
    initialContent?: string;
    initialImageUrl?: string;
    initialGroupIds?: string[];
    facilityId: string;
    currentResidentId: string;
    isEdit: boolean;
    isSubmitting: boolean;
}

// Post-body cap; posts-specific, so it stays here. The image limits moved to
// utils/fileValidation so the comment composers can share them without importing
// from the Posts tree.
export const MAX_CHARS = 2000;

const PostFormModal = ({
    open,
    onClose,
    onSubmit,
    initialContent = '',
    initialImageUrl = '',
    initialGroupIds = [],
    facilityId,
    currentResidentId,
    isEdit,
    isSubmitting,
}: PostFormModalProps) => {
    const [content, setContent] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState('');
    const [imageError, setImageError] = useState('');
    const [removeImage, setRemoveImage] = useState(false);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [groupOptions, setGroupOptions] = useState<{ key: string; value: string; text: string }[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const wasOpenRef = useRef(false);

    // Seed the form only on the closed -> open transition. Some of the initial*
    // props are fresh array/object literals, so keying the effect on them would
    // wipe whatever the user has typed whenever the parent happens to re-render.
    useEffect(() => {
        const justOpened = open && !wasOpenRef.current;
        wasOpenRef.current = open;
        if (!justOpened) return;

        setContent(initialContent);
        setImageFile(null);
        setImagePreview(initialImageUrl);
        setImageError('');
        setRemoveImage(false);
        setSelectedGroupIds(initialGroupIds);
    }, [open, initialContent, initialImageUrl, initialGroupIds]);

    useEffect(() => {
        if (!open) {
            setImageFile(null);
            setImagePreview(prev => {
                if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
                return '';
            });
            setImageError('');
        }
    }, [open]);

    useEffect(() => {
        return () => {
            if (imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    useEffect(() => {
        let cancelled = false;
        const loadGroups = async () => {
            if (!open || !facilityId || !currentResidentId) return;
            try {
                setGroupsLoading(true);
                const groups = await fetchAllResidentGroups(facilityId, String(currentResidentId));
                if (cancelled) return;
                const options = (groups || []).map((g: Group) => ({
                    key: String(g._id),
                    value: String(g._id),
                    text: g.Name || String(g._id),
                }));
                setGroupOptions(options);
            } catch {
                if (!cancelled) setGroupOptions([]);
            } finally {
                if (!cancelled) setGroupsLoading(false);
            }
        };
        loadGroups();
        return () => { cancelled = true; };
    }, [open, facilityId, currentResidentId]);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            setImageError('Only JPEG, PNG, GIF, and WebP images are allowed');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setImageError('Image must be under 5MB');
            return;
        }

        setImageError('');
        if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        setRemoveImage(false);
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return '';
        });
        setRemoveImage(true);
        setImageError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async () => {
        if (!content.trim()) return;
        try {
            await onSubmit(content.trim(), imageFile, removeImage, selectedGroupIds);
        } catch {
            // parent handles error display
        }
    };

    const canSubmit = content.trim().length > 0 && content.length <= MAX_CHARS && !isSubmitting;

    return (
        <Modal open={open} onClose={onClose} size="small" closeOnDimmerClick={!isSubmitting} className="post-form-modal">
            <Modal.Header>{isEdit ? 'Edit Post' : 'Create Post'}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.TextArea
                        placeholder="What's on your mind?"
                        value={content}
                        onChange={(e, data) => setContent(String(data.value || ''))}
                        maxLength={MAX_CHARS}
                        rows={5}
                    />
                    <div className="post-form-char-count">
                        {content.length}/{MAX_CHARS}
                    </div>

                    <Form.Field>
                        <label>Share to groups (optional)</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            placeholder="Select groups"
                            options={groupOptions}
                            loading={groupsLoading}
                            value={selectedGroupIds}
                            onChange={(_e, data) => setSelectedGroupIds((data.value as string[]) || [])}
                            disabled={isSubmitting}
                            noResultsMessage="No groups found"
                        />
                    </Form.Field>

                    <div className="post-form-image-section">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                        />

                        {imagePreview && (
                            <div className="post-form-image-preview">
                                <img src={imagePreview} alt="Preview" className="post-form-preview-img" />
                                <Button
                                    type="button"
                                    icon="close"
                                    size="mini"
                                    circular
                                    className="post-form-image-remove"
                                    onClick={handleRemoveImage}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}

                        {imageError && (
                            <div className="post-form-image-error">{imageError}</div>
                        )}

                        <Button
                            type="button"
                            className="post-form-image-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isSubmitting}
                        >
                            <Icon name="image outline" />
                            {imagePreview ? 'Change Image' : 'Add Image'}
                        </Button>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    primary
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    loading={isSubmitting}
                >
                    {isEdit ? 'Save' : 'Post'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default PostFormModal;
